import { debounce } from 'lodash';
import { useEffect, useRef } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { TRACK_PAGE_EVENT } from '@tools/ui/mutations';
import { EventType } from '@tools/ui/types/generated/graphql';
import { parseUrl } from './utils/mos-analytics';

const DEBOUNCE_TIME_MS = 2000;

interface UserStore {
  currentUser?: {
    _id?: string;
  };
}

export const usePageEventTracker = (userStore?: UserStore, path?: any) => {
  const [trackPageEventMutation] = useMutation(TRACK_PAGE_EVENT);
  const location = useLocation();
  const params = useParams();
  const lastTrackedLocationRef = useRef<string | null>(null);

  const debouncedTrackEventRef = useRef<ReturnType<typeof debounce> | null>(
    null
  );
  if (debouncedTrackEventRef.current === null) {
    debouncedTrackEventRef.current = debounce(
      (
        currentUrl: string,
        routeParamsInternal: Record<string, string | undefined>
      ) => {
        if (!userStore?.currentUser?._id) {
          return;
        }

        try {
          const eventData = {
            eventType: EventType.PageLoadEvent,
            pageTitle: typeof document !== 'undefined' ? document.title : '',
            url: parseUrl(currentUrl, routeParamsInternal),
            rawUrl: currentUrl
          };

          trackPageEventMutation({
            variables: {
              input: eventData
            }
          })
            .then(() => {
              lastTrackedLocationRef.current = currentUrl;
            })
            .catch((error) => {
              console.error('Failed to track page event', { error });
            });
        } catch (error) {
          console.error('Error preparing page event', { error });
        }
      },
      DEBOUNCE_TIME_MS
    );
  }

  useEffect(() => {
    const currentUrl =
      typeof window !== 'undefined' ? window.location.href : '';

    // there might be multiple routers on a page, so only the one matching the full path should track the page
    const shouldTrack =
      path && matchPath(location.pathname, { path, exact: true });

    if (
      currentUrl &&
      currentUrl !== lastTrackedLocationRef.current &&
      shouldTrack
    ) {
      debouncedTrackEventRef.current?.(
        currentUrl,
        params as Record<string, string | undefined>
      );
    }
    return () => {
      debouncedTrackEventRef.current?.cancel();
    };
  }, [location, params, path]);

  return null;
};
